import React, { useEffect, useState } from 'react';
import { X, RefreshCcw } from 'lucide-react';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable modal to issue a (full or partial) refund against a receipt,
 * paid invoice, or quote payment.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - sourceType: 'receipt' | 'invoice' | 'quote_payment'
 *  - sourceId: string
 *  - sourceNumber: string (e.g. "PV-2026-001")
 *  - paidAmount: number   (the gross paid against this source)
 *  - currency: string     (e.g. "CI$")
 *  - clientName: string
 *  - quotePaymentId?: string (when refunding one quote payment entry)
 *  - onIssued: (refund) => void   (callback after successful create)
 */
export const IssueRefundModal = ({
  open, onClose, sourceType, sourceId, sourceNumber,
  paidAmount, currency = 'CI$', clientName, quotePaymentId, onIssued,
}) => {
  const password = sessionStorage.getItem('admin_password');
  const [priorRefunded, setPriorRefunded] = useState(0);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [refundDate, setRefundDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [emailAfter, setEmailAfter] = useState(true);

  useEffect(() => {
    if (!open || !sourceType || !sourceId) return;
    setError('');
    setAmount('');
    setReason('');
    setEmailAfter(true);
    setRefundDate(new Date().toISOString().slice(0, 10));
    // Fetch any prior refunds against this source
    fetch(`${API_URL}/api/admin/refunds/by-source/${sourceType}/${sourceId}`, {
      headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
    })
      .then(r => r.ok ? r.json() : { total_refunded: 0 })
      .then(d => setPriorRefunded(d.total_refunded || 0))
      .catch(() => setPriorRefunded(0));
  }, [open, sourceType, sourceId]);

  if (!open) return null;

  const remaining = Math.max((paidAmount || 0) - priorRefunded, 0);
  const fmt = (n) => `${currency}${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      setError('Please enter a valid refund amount.');
      return;
    }
    if (amt > remaining + 0.005) {
      setError(`Refund cannot exceed the remaining refundable balance of ${fmt(remaining)}.`);
      return;
    }
    if (!reason.trim()) {
      setError('Please provide a reason for the refund.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/refunds`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(':' + password)}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          source_type: sourceType,
          source_id: sourceId,
          quote_payment_id: quotePaymentId || null,
          amount: amt,
          reason: reason.trim(),
          refund_date: new Date(refundDate).toISOString(),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Failed (HTTP ${res.status})`);
      }
      const refund = await res.json();

      if (emailAfter) {
        try {
          await fetch(`${API_URL}/api/admin/refunds/${refund.id}/send-email`, {
            method: 'POST',
            headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
          });
        } catch (em) {
          logger.error('Refund email failed:', em);
        }
      }

      onIssued && onIssued(refund);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to issue refund.');
    } finally {
      setSubmitting(false);
    }
  };

  const fillFull = () => setAmount(remaining.toFixed(2));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose} data-testid="issue-refund-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 text-red-600 rounded-lg flex items-center justify-center">
              <RefreshCcw className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">Issue Refund</h2>
              <p className="text-xs text-foreground/60">Against {sourceNumber} · {clientName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="refund-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="text-xs text-foreground/60 mb-1">Paid</div>
              <div className="font-semibold">{fmt(paidAmount)}</div>
            </div>
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
              <div className="text-xs text-amber-800 mb-1">Already refunded</div>
              <div className="font-semibold text-amber-700">{fmt(priorRefunded)}</div>
            </div>
            <div className="p-3 rounded-lg bg-red-50 border border-red-200">
              <div className="text-xs text-red-800 mb-1">Refundable</div>
              <div className="font-semibold text-red-700">{fmt(remaining)}</div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">Refund Amount ({currency})</label>
            <div className="flex gap-2">
              <input
                type="number" step="0.01" min="0.01" max={remaining}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                data-testid="refund-amount-input"
                className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-red-500"
                required
              />
              <button type="button" onClick={fillFull}
                      className="px-3 py-2 text-sm border-2 border-red-500 text-red-600 rounded-lg hover:bg-red-50">
                Full ({fmt(remaining)})
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">Refund Date</label>
            <input
              type="date"
              value={refundDate}
              onChange={(e) => setRefundDate(e.target.value)}
              data-testid="refund-date-input"
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-red-500"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">Reason for Refund</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="e.g. Flight cancelled, client withdrew booking, service not delivered…"
              data-testid="refund-reason-input"
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-red-500"
              required
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={emailAfter}
              onChange={(e) => setEmailAfter(e.target.checked)}
              data-testid="refund-email-after-checkbox"
            />
            <span>Email the refund receipt PDF to the client immediately</span>
          </label>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm" data-testid="refund-error">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
                    className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={submitting || remaining <= 0}
                    data-testid="issue-refund-submit-btn"
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2">
              <RefreshCcw className={`w-4 h-4 ${submitting ? 'animate-spin' : ''}`} />
              {submitting ? 'Issuing…' : 'Issue Refund'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IssueRefundModal;
